import CustomButton from 'components/CustomInput/Button';
import React, { useState } from 'react';
import TextHeader from 'components/CustomText/TextHeader';
import { Grid, Checkbox } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TextSubHeader from 'components/CustomText/TextSubHeader';
import CustomTextField from 'components/Input/CustomTextField';
import CustomDatePicker from 'components/Input/CustomDatePicker';
import CustomTable from 'components/CustomTable';
import PopupInput from 'components/Application/TeamMemberForm/PopupInput';
import FormRowItemContent from 'components/Global/FormRowItemContent';
import FollowUpActions from './FollowUpActions';
import DownloadStaticFileLink from 'components/CustomLink/DownloadStaticFileLink';
import useUploadDocData from './useUploadDocData';
import GeneralPostApprovalLayout from 'components/GeneralPostApprovalLayout';
import useQuery from 'hooks/useQuery';
import { useEffect } from 'react';
import { UP_DATA_INITIAL } from 'store/key';
import TipsIcon from 'components/TipsIcon';
import { useDispatch, useSelector } from 'react-redux';
import { SUSAR_REPORT } from 'utility/postApprovalLookUp';
import { getTimeWithTimeZone } from 'utility/general';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { useHistory } from 'react-router';
import { SAVE_SUSAR, GET_SUSAR, SUBMIT_SUSAR, SET_SUSAR_REPORT } from 'store/key';
import { GENERAL_SAVE_SUCCESS, GENERAL_SUBMIT_SUCCESS } from 'utility/frontendMessage';
import useDialog from 'components/ConfirmDialog/useDialog';
import { SEvenRow } from 'style/CommonStyledComponent';
import { postApprovalPermissionChecking } from 'utility/general';

const useStyles = makeStyles((theme) => ({
    page: {
        padding: 15,
    },
    itemContent: {
        paddingTop: 20,
    },
    subItem: {
        paddingTop: 10,
    },
    textSubHeader: {
        paddingBottom: 10,
    },
    leftColumn: {
        paddingRight: 30,
    },
    rightColumn: {
        paddingLeft: 30,
    },
    italicText: {
        paddingTop: 5,
        fontStyle: 'italic',
    },
}));

const SUSARReport = (props) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const showDialog = useDialog();
    let appId = useQuery('appId');
    if (appId) {
        appId = Number(appId);
    }
    const reportId = useQuery('reportId');
    const location = useLocation();
    const history = useHistory();

    const susarReport = useSelector((state) => state.susarReport);
    const remoteDetail = susarReport.remoteDetail;
    const rows = susarReport.rows;
    const isLoading = susarReport.isLoading;
    const isNotYetStarted = susarReport.remoteDetail.isNotYetStarted;
    const approvalDecisionDate = susarReport.remoteDetail.decisionDate;
    const forceErrorDisplay = susarReport.forceErrorDisplay;
    const status = susarReport.remoteDetail.lastStatus;
    const hasCCOAction = susarReport.remoteDetail.hasCCOAction;

    const signedFileId = susarReport.remoteDetail.signedFileId;
    const signedFileName = susarReport.remoteDetail.signedFileName;
    const [signedUpload, setSignedUpload] = useState({ fileName: '', key: null });

    useEffect(() => {
        if (signedFileId || signedFileName) {
            setSignedUpload({ fileName: signedFileName, key: signedFileId });
        } else {
            setSignedUpload({ fileName: '', key: null });
        }
    }, [signedFileId, signedFileName]);

    const isPermission = postApprovalPermissionChecking({ status, reportData: susarReport.remoteDetail });
    let isViewOnly = !isPermission;

    const isFormCreate = !reportId;
    const docType = SUSAR_REPORT;

    const {
        columns: uploadDocColumns,
        defaultUploadDocFormList,
        onUploadDocEditSubmit,
        handleUploadDocUpdate,
        handleUploadDocDelete,
        showUploadDocDialog,
        setShowUploadDocDialog,
    } = useUploadDocData({ susarReport, rows });

    const [actualStudyStartDateValid, setActualStudyStartDateValid] = useState(true);
    const [recruitedSubjectNoValid, setRecruitedSubjectNoValid] = useState(true);
    const [totalNumberReportsSubmittedValid, setTotalNumberReportsSubmittedValid] = useState(true);

    const checkIsFormDataChanged = () => {
        if (susarReport.remoteDetail.addSupplements.length || susarReport.remoteDetail.deleteSupplements.length) {
            return true;
        }

        let isChanged = false;
        for (let key in susarReport.oldData) {
            if (['actualStudyStartDate'].includes(key)) {
                if (
                    new Date(susarReport.oldData[key]).getTime() !== new Date(susarReport.remoteDetail[key]).getTime()
                ) {
                    isChanged = true;
                    break;
                }
            } else if (susarReport.oldData[key] !== susarReport.remoteDetail[key]) {
                isChanged = true;
                break;
            }
        }

        return isChanged;
    };

    const getSusarDetail = () => {
        dispatch({
            type: GET_SUSAR,
            payload: {
                docType,
                reportId,
                appId,
                isFormCreate,
                onPermissionError: () => {
                    history.push('/');
                },
            },
        });
    };

    useEffect(() => {
        getSusarDetail();
        // eslint-disable-next-line
    }, [reportId]);

    const handleChange = (name, value) => {
        let data = { ...remoteDetail, [name]: value };
        dispatch({
            type: UP_DATA_INITIAL,
            payload: {
                key: 'remoteDetail',
                value: data,
            },
        });
    };

    const handleFormCreate = ({ callback }) => {
        const { pathname } = location;
        dispatch({
            type: SAVE_SUSAR,
            payload: {
                reportId,
                appId,
                isFormCreate,
                signedFileId: signedUpload.key,
                callback: (newReportId) => {
                    showDialog({
                        description: GENERAL_SAVE_SUCCESS,
                        disableCancelButton: true,
                    });

                    if (isFormCreate) {
                        callback?.(newReportId);
                        history.push(`${pathname}?reportId=${newReportId}`);
                    } else {
                        getSusarDetail();
                        callback?.();
                    }
                },
            },
        });
    };

    const checkSubmitFormValid = () => {
        dispatch({
            type: SET_SUSAR_REPORT,
            payload: {
                forceErrorDisplay: true,
            },
        });
        let valid = true;
        if (!actualStudyStartDateValid) valid = false;
        if (!recruitedSubjectNoValid) valid = false;
        if (!totalNumberReportsSubmittedValid) valid = false;
        if (susarReport.remoteDetail.anyRecommendation === null) valid = false;
        if (susarReport.remoteDetail.isAlterationRequired === null) valid = false;

        return { valid: valid };
    };

    const onSubmit = async () => {
        dispatch({
            type: SUBMIT_SUSAR,
            payload: {
                reportId,
                appId,
                isFormCreate,
                signedFileId: signedUpload.key,
                callback: () => {
                    showDialog({
                        description: GENERAL_SUBMIT_SUCCESS,
                        disableCancelButton: true,
                    });
                    history.push('/');
                },
            },
        });
    };

    const changeNotYetStarted = (e) => {
        const value = e.target.checked ? true : null;
        if (value) {
            dispatch({
                type: SET_SUSAR_REPORT,
                payload: {
                    remoteDetail: {
                        ...remoteDetail,
                        actualStudyStartDate: null,
                        isNotYetStarted: value,
                    },
                },
            });
        } else {
            handleChange('isNotYetStarted', value);
        }
    };

    return (
        <GeneralPostApprovalLayout
            type={docType}
            isFormCreate={isFormCreate}
            onCreate={handleFormCreate}
            onSubmit={onSubmit}
            isLoading={isLoading}
            isViewOnly={isViewOnly}
            status={status}
            reportId={reportId}
            hasCCOAction={hasCCOAction}
            isPermission={isPermission}
            signedUpload={signedUpload} // case 1: object from server, case 2: object from local upload
            onSignedDocumentUploaded={(result) => {
                // result is in format {fileName: 'test.docx', key: 112, isNewUpload: true }
                // isNewUpload indicate the file is uploaded but not saved in form.
                setSignedUpload(result);
            }}
            checkIsFormDataChanged={checkIsFormDataChanged}
            checkSubmitFormValid={checkSubmitFormValid}
            siteInfo={remoteDetail?.siteInstruction}
        >
            {showUploadDocDialog && (
                <PopupInput
                    title={'Upload Supplementary Document'}
                    height="500px"
                    width="700px"
                    alignItems={'center'}
                    defaultFormList={defaultUploadDocFormList}
                    editRow={onUploadDocEditSubmit}
                    setOpenPopup={setShowUploadDocDialog}
                    tableList={rows}
                    onSubmit={handleUploadDocUpdate}
                    isUpload={true}
                />
            )}
            <Grid>
                <TextHeader style={{ textAlign: 'center', marginBottom: 10 }}>
                    Overseas Suspected Unexpected Serious Adverse Reaction (SUSAR) Report Form
                </TextHeader>
            </Grid>
            <Grid className={classes.itemContent}>
                <TextSubHeader className={classes.textSubHeader}>1. Basic Information</TextSubHeader>

                <SEvenRow style={{ marginTop: 10 }}>
                    <div>
                        <div>Scientific Title:</div>
                        <div style={{ wordBreak: 'break-word' }}>{remoteDetail?.scientificTitle}</div>
                    </div>
                </SEvenRow>
                <SEvenRow style={{ marginTop: 20 }}>
                    <div>
                        <div>IRB/REC Ref. No.:</div>
                        <div style={{ wordBreak: 'break-word' }}>{remoteDetail?.refNo}</div>
                    </div>
                    <div>
                        <div>Protocol No.:</div>
                        <div style={{ wordBreak: 'break-word' }}>{remoteDetail?.protocolNo}</div>
                    </div>
                </SEvenRow>
                <SEvenRow style={{ marginTop: 20 }}>
                    <div>
                        <div>Coordinating Investigator:</div>
                        <div style={{ wordBreak: 'break-word' }}>{remoteDetail?.ci}</div>
                    </div>
                    <div>
                        <div>Anticipated Study End Date:</div>
                        <div>
                            {remoteDetail?.antipicateStudyEndDate
                                ? getTimeWithTimeZone(remoteDetail.antipicateStudyEndDate).format('YYYY-MM-DD')
                                : '-'}
                        </div>
                    </div>
                </SEvenRow>

                <SEvenRow style={{ marginTop: 20 }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            Actual Study Start Date:
                            <TipsIcon title={['Before current date and not earlier than approval decision date']} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ width: 200 }}>
                                <CustomDatePicker
                                    value={remoteDetail?.actualStudyStartDate}
                                    onChange={(date) => handleChange('actualStudyStartDate', new Date(date))}
                                    disabled={isViewOnly || isNotYetStarted}
                                    minDate={new Date(approvalDecisionDate).getTime()}
                                    maxDate={new Date().getTime()}
                                    validators={isNotYetStarted ? [] : ['isRequired']}
                                    forceErrorDisplay={forceErrorDisplay}
                                    isValidValue={(isValid) => setActualStudyStartDateValid(isValid)}
                                />
                            </div>
                            <Checkbox
                                size="small"
                                color="primary"
                                disabled={isViewOnly}
                                onChange={changeNotYetStarted}
                                checked={remoteDetail ? remoteDetail.isNotYetStarted : null}
                            />
                            Not yet started
                        </div>
                    </div>
                </SEvenRow>

                <SEvenRow style={{ marginTop: 10 }}>
                    <div>
                        <div>Planned Sample Size (total):</div>
                        <div>{remoteDetail?.plannedSampleSize}</div>
                    </div>
                </SEvenRow>

                <FormRowItemContent
                    isSigleRow
                    sigleRowTitleXs={3}
                    leftChildren={{
                        title: 'Actual No. of Recruited Subjects as at the report date of this SUSAR (total):',
                        value: (
                            <CustomTextField
                                label="Actual No. of Recruited Subjects as at the report date of this SUSAR (total)"
                                value={remoteDetail?.recruitedSubjectNo}
                                forceErrorDisplay={forceErrorDisplay}
                                validators={['isRequired']}
                                numberField
                                minNumber={0}
                                style={{ width: 500 }}
                                isValidValue={(isValid) => setRecruitedSubjectNoValid(isValid)}
                                disabled={isViewOnly}
                                onChange={(e) => handleChange('recruitedSubjectNo', Number(e.target.value))}
                            />
                        ),
                    }}
                />
            </Grid>
            <Grid className={classes.itemContent}>
                <TextSubHeader>2. Submitted Information</TextSubHeader>
                <Grid container wrap="nowrap" className={classes.subItem} alignItems="center">
                    Total No. of Reports Submitted:
                    <CustomTextField
                        style={{ marginLeft: 10 }}
                        label="Total No. of Reports Submitted"
                        value={remoteDetail?.totalNumberReportsSubmitted}
                        forceErrorDisplay={forceErrorDisplay}
                        validators={['isRequired']}
                        disabled={isViewOnly}
                        isValidValue={(isValid) => setTotalNumberReportsSubmittedValid(isValid)}
                        numberField
                        minNumber={0}
                        onChange={(e) => handleChange('totalNumberReportsSubmitted', Number(e.target.value))}
                    />
                    {!isViewOnly && (
                        <DownloadStaticFileLink
                            style={{ padding: '0 10px' }}
                            isChangeColor
                            href={
                                process.env.PUBLIC_URL +
                                '/files/SUSAR report template for download - draft 20220214.docx'
                            }
                            name={'SUSARReportTemplate'}
                        >
                            Download SUSAR report template
                        </DownloadStaticFileLink>
                    )}
                </Grid>
            </Grid>
            <Grid className={classes.itemContent}>
                <TextSubHeader>3. Follow-up Actions</TextSubHeader>
                <FollowUpActions
                    classes={classes}
                    viewOnly={isViewOnly}
                    reportData={remoteDetail}
                    handleChange={handleChange}
                    forceErrorDisplay={forceErrorDisplay}
                />
            </Grid>
            <Grid className={classes.itemContent}>
                <TextSubHeader>Upload Supplementary Document</TextSubHeader>
                {!isViewOnly && (
                    <CustomButton style={{ marginTop: 8 }} onClick={() => setShowUploadDocDialog(true)}>
                        Add
                    </CustomButton>
                )}
                <Grid className={classes.subItem}>
                    <CustomTable
                        disabled={isViewOnly}
                        columns={uploadDocColumns}
                        rows={rows}
                        isDeleteAllow={!isViewOnly}
                        onDelete={handleUploadDocDelete}
                    />
                </Grid>
            </Grid>
        </GeneralPostApprovalLayout>
    );
};

export default SUSARReport;
